/**
 * OperatorRecentDraws — last 7 days of lottery results for the states this
 * location has enabled. Grouped by state, with midday/evening columns.
 *
 * Backed by GET /api/operator/lottery/draws?days=7
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { RefreshCw, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STATE_NAMES = { IL: 'Illinois', MS: 'Mississippi', TN: 'Tennessee' };
const STATE_COLORS = {
    IL: 'border-blue-500/40 from-blue-950/30',
    MS: 'border-purple-500/40 from-purple-950/30',
    TN: 'border-orange-500/40 from-orange-950/30',
};

const OperatorRecentDraws = ({ endpoint = '/operator/lottery/draws', pollMs = 30000 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}${endpoint}?days=7`, { withCredentials: true });
            setData(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [endpoint]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh every `pollMs` so the cashier/operator sees newly
    // posted draws without manually hitting refresh. Stops if the tab is
    // hidden (browser pauses setInterval anyway, but be explicit).
    useEffect(() => {
        if (!pollMs) return;
        const handle = setInterval(load, pollMs);
        return () => clearInterval(handle);
    }, [load, pollMs]);

    if (loading && !data) {
        return (
            <div className="text-center py-12 text-zinc-500" data-testid="draws-loading">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading recent drawings…
            </div>
        );
    }
    if (!data) return null;

    // Group: state → { [date]: { midday: {pick3,pick4}, evening: {pick3,pick4} } }
    const byState = {};
    for (const s of data.states) byState[s] = {};
    for (const d of data.draws) {
        if (!byState[d.state]) byState[d.state] = {};
        const dateMap = byState[d.state];
        if (!dateMap[d.draw_date]) dateMap[d.draw_date] = { midday: {}, evening: {} };
        const slot = dateMap[d.draw_date][d.drawing_time] || {};
        slot[`pick${d.pick_type}`] = d.winning_numbers;
        dateMap[d.draw_date][d.drawing_time] = slot;
    }

    return (
        <div className="space-y-5" data-testid="operator-recent-draws">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-zinc-100">Recent drawings · last 7 days</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Drawings shown for states enabled at your location ({data.states.join(', ') || 'none'}).
                    </p>
                </div>
                <Button onClick={load} size="sm" variant="outline"
                    className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                    data-testid="draws-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            {data.states.length === 0 && (
                <div className="text-zinc-500 text-sm bg-zinc-900/30 border border-zinc-800 rounded-md p-6 text-center">
                    No lottery states are enabled for your location. Enable them from Settings → Location features.
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {data.states.map(st => (
                    <StateColumn key={st}
                        state={st}
                        days={byState[st] || {}}
                        testKey={st}
                    />
                ))}
            </div>
        </div>
    );
};

const StateColumn = ({ state, days, testKey }) => {
    const colorCls = STATE_COLORS[state] || 'border-zinc-700 from-zinc-900/30';
    const sortedDates = Object.keys(days).sort().reverse();

    return (
        <div className={`rounded-md border bg-gradient-to-br to-zinc-900/30 ${colorCls}`}
            data-testid={`state-card-${testKey}`}>
            <div className="px-4 py-3 border-b border-zinc-800/60">
                <h4 className="font-semibold text-zinc-100">{STATE_NAMES[state] || state}</h4>
                <p className="text-[11px] text-zinc-500 mt-0.5">{sortedDates.length} drawing day{sortedDates.length === 1 ? '' : 's'}</p>
            </div>
            <div className="divide-y divide-zinc-800/60">
                {sortedDates.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-zinc-500">
                        No drawings in the last 7 days
                    </div>
                )}
                {sortedDates.map(date => (
                    <div key={date} className="px-4 py-3" data-testid={`draw-day-${testKey}-${date}`}>
                        <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">
                            {formatDate(date)}
                        </p>
                        <TimeSlot label="Midday"  draws={days[date].midday} />
                        <TimeSlot label="Evening" draws={days[date].evening} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const TimeSlot = ({ label, draws }) => {
    if (!draws || (!draws.pick3 && !draws.pick4)) return null;
    return (
        <div className="flex items-center gap-3 py-1">
            <span className="text-[11px] text-zinc-400 w-14">{label}</span>
            <div className="flex flex-wrap gap-2 flex-1">
                {draws.pick3 && <PickBalls label="P3" nums={draws.pick3} />}
                {draws.pick4 && <PickBalls label="P4" nums={draws.pick4} />}
            </div>
        </div>
    );
};

const PickBalls = ({ label, nums }) => (
    <div className="flex items-center gap-1">
        <span className="text-[10px] text-zinc-500 font-mono">{label}</span>
        {nums.map((n, i) => (
            <span key={i} className="inline-flex items-center justify-center w-6 h-6 rounded-full
                bg-zinc-950 border border-zinc-700 text-emerald-300 font-mono text-xs font-bold">
                {n}
            </span>
        ))}
    </div>
);

const formatDate = (iso) => {
    try {
        const [y, m, d] = iso.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, {
            weekday: 'short', month: 'short', day: 'numeric',
        });
    } catch { return iso; }
};

export default OperatorRecentDraws;
